/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { DIR_DOCUMENT, Direction, Directionality } from '@angular/cdk/bidi';
import { Inject, Injectable, Optional } from '@angular/core';
import { LanguageCodeHelper } from '../../helpers/language-code-helper';
import { TextOrientation } from '../../models/languages/languages.model';
import { Organization } from '../../models/organization/organization.model';

/** Provided for the `Directionality` token in `app.module`, so CDK overlays follow language switches. */
@Injectable({ providedIn: 'root' })
export class V3DirectionService extends Directionality {
  constructor(@Optional() @Inject(DIR_DOCUMENT) private readonly document?: Document) {
    super(document);
    this.applyToDocument(LanguageCodeHelper.getBodyLanguageDir());
  }

  /** Applies both the direction and `<html lang>` for one of the organisation's languages. */
  applyOrganizationLanguage(organization: Organization | null | undefined, languageCode: string | null | undefined): void {
    if (!organization) {
      return;
    }
    this.setFromOrientation(organization.languages?.find((lang) => lang.code === languageCode)?.orientation);
    this.setDocumentLanguage(languageCode);
  }

  setFromOrientation(orientation: TextOrientation | null | undefined): void {
    if (!orientation) {
      return;
    }
    this.setDirection(orientation === 'RIGHT_TO_LEFT' ? 'rtl' : 'ltr');
  }

  setDirection(direction: Direction): void {
    if (this.value === direction) {
      return;
    }

    LanguageCodeHelper.setBodyLanguageDir(direction === 'rtl' ? 'RIGHT_TO_LEFT' : 'LEFT_TO_RIGHT');
    this.applyToDocument(direction);
    this.change.emit(direction);
  }

  private setDocumentLanguage(languageCode: string | null | undefined): void {
    const root = this.document?.documentElement;
    if (!languageCode || !root || root.lang === languageCode) {
      return;
    }
    root.lang = languageCode;
  }

  private applyToDocument(direction: Direction): void {
    // `Directionality.value` is declared readonly for consumers; this service owns writing it.
    (this as unknown as { value: Direction }).value = direction;

    this.document?.body?.setAttribute('dir', direction);
    this.document?.documentElement?.setAttribute('dir', direction);
  }
}
